import Footer from '@/components/footer';
import Logo from '@/components/logo';
import { Button } from '@/components/ui/button';
import useAuth from '@/hooks/use-auth';
import {
    AlertTriangle,
    Home,
    Key,
    Loader,
    LogIn,
    Settings,
    Shield,
    UserPlus,
} from 'lucide-react';
import React from 'react';
import { useNavigate } from 'react-router-dom';

interface NotFoundProps {
    isAuthenticated?: boolean;
}

const NotFound: React.FC<NotFoundProps> = () => {
    const navigate = useNavigate();
    const { data: isAuthenticated, isPending } = useAuth();

    const handleNavigate = (path: string) => {
        navigate(path);
    };

    const primaryRoutes = isAuthenticated
        ? [
              {
                  path: '/mfa',
                  label: 'MFA Settings',
                  icon: <Key className='size-4 md:!size-6' />,
                  description: 'Go to Multi-Factor Authentication',
                  style: 'btn-primary',
              },
          ]
        : [
              {
                  path: '/login',
                  label: 'Login',
                  icon: <LogIn className='!size-4 md:!size-5' />,
                  description: 'Sign in to your account',
                  style: 'btn-login',
              },
              {
                  path: '/signup',
                  label: 'Sign Up',
                  icon: <UserPlus className='!size-4 md:!size-5' />,
                  description: 'Create a new account',
                  style: 'btn-signup',
              },
          ];

    const secondaryRoutes = [
        {
            path: isAuthenticated ? '/settings' : '/',
            label: isAuthenticated ? 'Settings' : 'Home',
            icon: isAuthenticated ? (
                <Settings className='!size-5 text-background' />
            ) : (
                <Home size={20} />
            ),
            description: isAuthenticated
                ? 'Back to Settings'
                : 'Back to homepage',
            style: 'btn-secondary',
        },
    ];

    return (
        <div className='min-h-screen bg-gradient-to-br from-slate-900 to-slate-700'>
            <header className='shadow-sm border-b border-slate-700'>
                <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
                    <div className='flex justify-between items-center h-16'>
                        <div className='flex items-center space-x-3'>
                            <div className='flex items-center justify-center'>
                                <Logo size='60px' />
                            </div>
                            <span className='text-xl font-bold text-slate-300'>
                                Advanced Sessions Management
                            </span>
                        </div>
                        <button
                            onClick={() => handleNavigate('/')}
                            className='flex items-center space-x-2 px-4 py-2 text-sm font-medium text-slate-300 hover:text-slate-50 transition-colors'>
                            <Home size={16} />
                            <span>Home</span>
                        </button>
                    </div>
                </div>
            </header>

            {isPending ? (
                <div className='flex justify-center items-center h-screen'>
                    <Loader className='size-8 animate-spin' />
                </div>
            ) : (
                <>
                    <main className='max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16'>
                        <div className='text-center'>
                            <div className='flex justify-center mb-8'>
                                <div className='relative'>
                                    <div className='w-32 h-32 bg-gradient-to-br from-red-100 to-orange-100 rounded-full flex items-center justify-center'>
                                        <AlertTriangle className='h-16 w-16 text-red-500' />
                                    </div>
                                    <div className='absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-12 h-12 flex items-center justify-center text-2xl font-bold'>
                                        404
                                    </div>
                                </div>
                            </div>

                            <h1 className='text-5xl font-bold mb-6'>
                                Page Not Found
                            </h1>
                            <p className='text-xl text-slate-300 mb-12 max-w-2xl mx-auto leading-relaxed'>
                                The page you&apos;re looking for doesn&apos;t
                                exist or has been moved.
                                <br />
                                {isAuthenticated
                                    ? " Let's get you back to your MFA Settings."
                                    : " Don't worry, you can sign in or create a new account to continue."}
                            </p>

                            <div className='flex flex-col sm:flex-row gap-4 justify-center mb-12'>
                                {primaryRoutes.map((route) => (
                                    <Button
                                        key={route.path}
                                        title={route.description}
                                        onClick={() =>
                                            handleNavigate(route.path)
                                        }
                                        className={`
                  flex items-center justify-center space-x-3 px-8 py-4 h-14 rounded-xl font-semibold text-lg transition-all duration-200 transform hover:scale-105 hover:shadow-lg
                  ${route.style === 'btn-primary' ? 'bg-gradient-to-br from-purple-600 to-blue-500 text-white hover:from-purple-700 hover:to-blue-600 shadow-md' : ''}
                  ${route.style === 'btn-login' ? 'bg-white text-slate-800 border-2 border-slate-300 hover:border-slate-400 hover:bg-slate-50 shadow-sm' : ''}
                  ${route.style === 'btn-signup' ? 'bg-gradient-to-br from-purple-600 to-blue-500 text-white hover:from-purple-700 hover:to-blue-600 shadow-md' : ''}
                `}>
                                        {route.icon}
                                        <span>{route.label}</span>
                                    </Button>
                                ))}
                            </div>

                            <div className='bg-white rounded-2xl shadow-sm border border-slate-200 p-8 max-w-2xl mx-auto'>
                                <h2 className='text-2xl font-semibold text-slate-800 mb-6'>
                                    Quick Navigation
                                </h2>
                                <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                                    {secondaryRoutes.map((route) => (
                                        <button
                                            key={route.path}
                                            onClick={() =>
                                                handleNavigate(route.path)
                                            }
                                            className='flex items-center space-x-4 p-6 text-left rounded-xl border-2 border-slate-200 hover:border-purple-300 hover:bg-purple-50 transition-all duration-200 group'>
                                            <div className='flex items-center justify-center w-12 h-12 bg-slate-100 rounded-lg group-hover:bg-white transition-colors'>
                                                {route.icon}
                                            </div>
                                            <div>
                                                <h3 className='font-semibold text-slate-800 group-hover:text-purple-700 transition-colors'>
                                                    {route.label}
                                                </h3>
                                                <p className='text-sm text-slate-600 mt-1'>
                                                    {route.description}
                                                </p>
                                            </div>
                                        </button>
                                    ))}

                                    <div className='flex items-center space-x-4 p-6 rounded-xl border-2 border-slate-200 bg-slate-50'>
                                        <div className='flex items-center justify-center w-12 h-12 bg-slate-200 rounded-lg'>
                                            <Shield
                                                size={20}
                                                className='text-slate-500'
                                            />
                                        </div>
                                        <div>
                                            <h3 className='font-semibold text-slate-700'>
                                                Need Help?
                                            </h3>
                                            <p className='text-sm text-slate-600 mt-1'>
                                                Contact our support team
                                            </p>
                                        </div>
                                    </div>

                                    <div className='flex items-center space-x-4 p-6 rounded-xl border-2 border-slate-200 bg-slate-50'>
                                        <div className='flex items-center justify-center w-12 h-12 bg-slate-200 rounded-lg'>
                                            <AlertTriangle
                                                size={20}
                                                className='text-slate-500'
                                            />
                                        </div>
                                        <div>
                                            <h3 className='font-semibold text-slate-700'>
                                                Report Issue
                                            </h3>
                                            <p className='text-sm text-slate-600 mt-1'>
                                                Let us know about this error
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className='mt-12 p-6 bg-slate-800 rounded-2xl text-slate-200 max-w-2xl mx-auto'>
                                <h3 className='font-semibold mb-3 text-slate-300'>
                                    Technical Details
                                </h3>
                                <div className='text-sm font-mono bg-slate-900 rounded-lg p-4'>
                                    <div className='flex flex-wrap gap-4'>
                                        <div>
                                            <span className='text-slate-400'>
                                                Status:
                                            </span>
                                            <span className='ml-2 text-red-400'>
                                                404 Not Found
                                            </span>
                                        </div>
                                        <div>
                                            <span className='text-slate-400'>
                                                Path:
                                            </span>
                                            <span className='ml-2 text-slate-300'>
                                                {window.location.pathname}
                                            </span>
                                        </div>
                                        <div>
                                            <span className='text-slate-400'>
                                                Auth:
                                            </span>
                                            <span className='ml-2 text-slate-300'>
                                                {isAuthenticated
                                                    ? 'Authenticated'
                                                    : 'Not Authenticated'}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </main>
                    <Footer isAuthenticated={Boolean(isAuthenticated)} />
                </>
            )}
        </div>
    );
};

export default NotFound;
